import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const fetchMe = async () => {
  const response = await axios({
    method: "post",
    url: `${process.env.REACT_APP_API_URL}/auth/me`,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
  return response.data;
};

function TasksManagement() {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [rowCount, setRowCount] = React.useState(0);
  const itemsPerPage = 9;

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    retry: false,
  });

  const { data: formsData, isLoading } = useQuery({
    queryKey: ["all-active-forms", page, itemsPerPage, meData?.user_id],
    queryFn: async () => {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/allforms/all`, {
        params: {
          user_id: meData?.user_id,
          page: page,
          limit: itemsPerPage,
        },
        withCredentials: true,
      });
      if (res.data.totalCount) {
        setRowCount(res.data.totalCount);
      }
      return res.data;
    },
    enabled: !!meData?.user_id,
  });

  const forms = React.useMemo(() => {
    if (formsData?.status === 200 && Array.isArray(formsData.data)) {
      return formsData.data;
    }
    return [];
  }, [formsData]);

  const totalPages = Math.ceil(rowCount / itemsPerPage);

  const isNew = (created_at) => {
    const createdDate = new Date(created_at);
    const now = new Date();
    const diffInHours = (now - createdDate) / (1000 * 60 * 60);
    return diffInHours <= 48; // New if created within 48 hours
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFillForm = (form) => {
    const formattedTitle = form.title.replaceAll(" ", "-");
    navigate(`/mission-tracker/add/${form.id}--${formattedTitle}`);
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
              <MDBox pt={3} px={2} pb={2}>
                {isLoading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : forms.length === 0 ? (
                  <MDBox textAlign="center" py={5}>
                    <MDTypography variant="h6" color="text" fontWeight="regular">
                      No active tasks found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox>
                    <Grid container spacing={3}>
                      {forms.map((form) => (
                        <Grid item xs={12} sm={6} md={4} key={form.id}>
                          <Card
                            sx={{
                              height: "250px", // Set a standard height
                              boxShadow: 3,
                              border: "1px solid #eee",
                              position: "relative",
                              overflow: "visible",
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {isNew(form.created_at) && (
                              <MDBox position="absolute" top={-10} right={-10} zIndex={1}>
                                <MDBadge
                                  badgeContent="New"
                                  color="error"
                                  variant="gradient"
                                  size="sm"
                                  circular
                                />
                              </MDBox>
                            )}
                            <MDBox p={3} display="flex" flexDirection="column" height="100%">
                              <MDBox mb={2}>
                                <Tooltip title={form.title} arrow>
                                  <MDTypography variant="h6" fontWeight="bold">
                                    {form.title?.length > 25
                                      ? form.title.slice(0, 25) + "..."
                                      : form.title}
                                  </MDTypography>
                                </Tooltip>
                                <Tooltip title={form.description || ""} arrow sx={{ mt: 1 }}>
                                  <MDTypography variant="button" color="text" fontWeight="regular">
                                    {form.description
                                      ? form.description.length > 35
                                        ? form.description.slice(0, 35) + "..."
                                        : form.description
                                      : "\u00A0"}
                                  </MDTypography>
                                </Tooltip>
                              </MDBox>
                              <MDBox mt="auto" display="flex" justifyContent="flex-end">
                                <MDButton
                                  variant="gradient"
                                  color="success"
                                  size="small"
                                  onClick={() => handleFillForm(form)}
                                  startIcon={<Icon>edit</Icon>}
                                >
                                  Fill Form
                                </MDButton>
                              </MDBox>
                            </MDBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <MDBox mt={4} display="flex" justifyContent="center">
                      <Pagination
                        count={Math.max(1, totalPages)}
                        page={page}
                        onChange={(e, value) => handlePageChange(value)}
                        color="info"
                        variant="outlined"
                        shape="rounded"
                      />
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout >
  );
}

export default TasksManagement;
